import GlobalStyle from "@/styles/GlobalStyle";
import type { AppProps } from "next/app";
import { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { I18nextProvider } from "react-i18next";
import locale from "@/utils/locale";
import { Provider } from "react-redux";
import wrapper from "@/redux";
import InquiryModal from "@/container/InquiryModal";
import FadeIn from "@/components/FadeIn";
import Popup from "@/components/Popup";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import AOS from "aos";
import "aos/dist/aos.css"; // You can also use <link> for styles
import useFadeIn from "@/hooks/useFadeIn";
import { ThemeProvider } from "styled-components";
import { theme } from "@/styles/theme";

export default function App({ Component, ...rest }: AppProps) {
  const { store, props } = wrapper.useWrappedStore(rest);

  const dimdElRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const isVisible = useFadeIn();

  useEffect(() => {
    AOS.init();
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    if (!loading) {
      if (dimdElRef.current) {
        dimdElRef.current.style.opacity = "0";
        dimdElRef.current.style.pointerEvents = "none";
      }
      const timeout = setTimeout(() => {
        setLoaded(true);
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  return (
    <Provider store={store}>
      <I18nextProvider i18n={locale}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          {!loaded ? (
            <Dimd ref={dimdElRef}>
              <LoadingIcon />
            </Dimd>
          ) : (
            <FadeIn isVisible={isVisible}>
              <Component {...props} />
              <Popup />
              <InquiryModal />
            </FadeIn>
          )}
        </ThemeProvider>
      </I18nextProvider>
    </Provider>
  );
}

const spinningAnimation = keyframes`
  0% {
    transform: scale(1) rotate(0);
  }
  50% {
    transform: scale(0.8) rotate(360deg);
  }
  100% {
    transform: scale(1) rotate(720deg);
  }
`;
const Dimd = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 1;
  transition-duration: 0.5s;
  background-color: #fff;
`;
const LoadingIcon = styled.div`
  background-repeat: no-repeat;
  background-position: center center;
  display: block;
  width: 100%;
  height: 100%;
  background-image: url("/InBody-favicon.png");
  animation: 2s cubic-bezier(0.63, 0.045, 0.34, 1) 0s normal none infinite ${spinningAnimation};
`;
